import type { Address, Hex } from 'viem'

/** Own Geth can serve a ~1.2M-block log query for one address. Fallback if a proxy rejects it. */
export const TEMPLATE_LOG_CHUNK = 50_000n

export const TEMPLATE_CACHE_VERSION = 1

export type MintedTemplateLog = {
  publisherUP: Address
  templateId: Hex
  version: number
  tokenId: Hex
  chainId: bigint
  permissions: bigint
  allowedCalls: Hex
  allowedERC725YDataKeys: Hex
  metadataURI: string
  blockNumber?: bigint
}

export type TemplateMintCache = {
  version: number
  address: Address
  lastBlock: string
  minted: MintedTemplateLog[]
}

export function templateMintCacheKey(registryAddress: Address): string {
  return `pc-template-mints-v${TEMPLATE_CACHE_VERSION}-${registryAddress.toLowerCase()}`
}

export function blockRanges(
  fromBlock: bigint,
  toBlock: bigint,
  chunk: bigint,
): Array<{ from: bigint; to: bigint }> {
  if (fromBlock > toBlock || chunk <= 0n) return []
  const ranges: Array<{ from: bigint; to: bigint }> = []
  for (let start = fromBlock; start <= toBlock; start += chunk) {
    const end = start + chunk - 1n > toBlock ? toBlock : start + chunk - 1n
    ranges.push({ from: start, to: end })
  }
  return ranges
}

/** One eth_getLogs for the full range; fall back to chunked scans if the node/proxy rejects it. */
export async function fetchLogsPreferUnchunked<T>(
  fetchRange: (from: bigint, to: bigint) => Promise<T[]>,
  fromBlock: bigint,
  toBlock: bigint,
  chunk: bigint = TEMPLATE_LOG_CHUNK,
): Promise<T[]> {
  if (fromBlock > toBlock) return []
  try {
    return await fetchRange(fromBlock, toBlock)
  } catch {
    const out: T[] = []
    for (const range of blockRanges(fromBlock, toBlock, chunk)) {
      out.push(...(await fetchRange(range.from, range.to)))
    }
    return out
  }
}

export function latestMintPerSeries(mints: MintedTemplateLog[]): MintedTemplateLog[] {
  const latest = new Map<string, MintedTemplateLog>()
  for (const mint of mints) {
    const key = `${mint.publisherUP.toLowerCase()}-${mint.templateId}`
    const existing = latest.get(key)
    if (!existing || mint.version > existing.version) {
      latest.set(key, mint)
    }
  }
  return [...latest.values()]
}

export function mergeMintedLogs(
  existing: MintedTemplateLog[],
  incoming: MintedTemplateLog[],
): MintedTemplateLog[] {
  const byToken = new Map<string, MintedTemplateLog>()
  for (const mint of [...existing, ...incoming]) {
    byToken.set(mint.tokenId.toLowerCase(), mint)
  }
  return [...byToken.values()]
}

export function readTemplateMintCache(registryAddress: Address): TemplateMintCache | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(templateMintCacheKey(registryAddress))
    if (!raw) return null
    const parsed = JSON.parse(raw) as TemplateMintCache
    if (parsed.version !== TEMPLATE_CACHE_VERSION) return null
    if (parsed.address.toLowerCase() !== registryAddress.toLowerCase()) return null
    return {
      ...parsed,
      minted: parsed.minted.map((mint) => ({
        ...mint,
        chainId: BigInt(mint.chainId),
        permissions: BigInt(mint.permissions),
        blockNumber: mint.blockNumber != null ? BigInt(mint.blockNumber) : undefined,
      })),
    }
  } catch {
    return null
  }
}

export function writeTemplateMintCache(cache: TemplateMintCache): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(
      templateMintCacheKey(cache.address),
      JSON.stringify({
        ...cache,
        minted: cache.minted.map((mint) => ({
          ...mint,
          chainId: mint.chainId.toString(),
          permissions: mint.permissions.toString(),
          blockNumber: mint.blockNumber?.toString(),
        })),
      }),
    )
  } catch {
    // Private mode / quota — discovery still works without cache.
  }
}
